import type { FleetSiteMapPoint } from "../../../ui/components/Map";
import type { DeviceHealthStatus } from "../../../ui/components/DeviceHealthIndicator";
import { deriveDeviceHealthStatus, resolveLastSignalAt } from "../../../ui/components/DeviceHealthIndicator/resolveLastSignalAt";
import type { ProductListItem } from "../../lib/api/products";

/** Map a device health status onto the map marker's status/color scheme. */
function toSiteMarkerStatus(status: DeviceHealthStatus): FleetSiteMapPoint["status"] {
  switch (status) {
    case "online":
    case "simulationActive":
      return "online";
    case "stale":
    case "simulationPaused":
      return "standby";
    case "initializing":
      return "commissioning";
    case "offline":
    case "unknown":
    default:
      return "offline";
  }
}

/**
 * Build a map marker point from a product list row, or `null` if the product
 * has no usable coordinates (excluded from the map view rather than erroring).
 */
export function toProductMapPoint(
  row: ProductListItem,
  ctx: {
    lastTelemetryAt?: string | null;
    lastShadowAt?: string | null;
    serviceStartedAt?: number;
    alarmCount?: number;
  },
): FleetSiteMapPoint | null {
  const latitude = row.latitude != null ? Number(row.latitude) : NaN;
  const longitude = row.longitude != null ? Number(row.longitude) : NaN;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

  const signalAt = resolveLastSignalAt(
    row.lastSignalAt,
    ctx.lastTelemetryAt ?? row.lastTelemetryAt ?? null,
    ctx.lastShadowAt ?? row.lastShadowAt ?? null,
  );
  const healthStatus = deriveDeviceHealthStatus(
    signalAt,
    row.createdAt,
    ctx.serviceStartedAt,
    row.isSimulated,
    row.isSimulating,
  );

  return {
    id: row.id,
    name: row.name,
    latitude,
    longitude,
    status: toSiteMarkerStatus(healthStatus),
    subtitle: [row.city, row.state].filter(Boolean).join(", ") || undefined,
    alarms: ctx.alarmCount ?? 0,
  };
}
